'use client';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { FormSectionTitle } from '../../Forms/FormSectionTitle';
import { Logo } from '../../Logo';
import { Button } from '../../Buttons/Button/Button';
import styles from './DetailModal.module.css';
import { CheckIcon } from 'lucide-react';

export interface DetailField {
	label: string;
	value: ReactNode;
	span?: 'full';
	showLabel?: boolean;
}

export interface DetailSection {
	title: string;
	fields: DetailField[];
	editContent?: ReactNode;
}

export interface DetailMetaItem {
	icon: ReactNode;
	text: string;
}

interface DetailModalProps {
	title: string;
	metaItems?: DetailMetaItem[];
	sections: DetailSection[];
	footer?: ReactNode;
	accentColor?: string;
	onClose: () => void;
	mode?: 'view' | 'edit' | 'create';
	onSubmit?: () => void;
	dismissible?: boolean;
}

export const DetailModal = ({
	title,
	metaItems,
	sections,
	footer,
	accentColor,
	onClose,
	mode = 'view',
	onSubmit,
	dismissible = true,
}: DetailModalProps) => {
	const isEditable = mode === 'edit' || mode === 'create';
	useEffect(() => {
		if (!dismissible) return;
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [dismissible, onClose]);

	return (
		<div className={styles.overlay} onClick={dismissible ? onClose : undefined}>
			<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
				<div className={styles.header}>
					<div className={styles.headerTop}>
						<Logo className={styles.logo} />
						<button type="button" className={styles.closeBtn} onClick={onClose}>
							<CloseOutlined fontSize="inherit" />
						</button>
					</div>

					<h2 className={styles.title}>{title}</h2>

					{metaItems && metaItems.length > 0 && (
						<div className={styles.metaRow}>
							{metaItems.map((item, i) => (
								<div key={i} className={styles.metaItem}>
									{item.icon}
									<span className={styles.metaText}>{item.text}</span>
								</div>
							))}
						</div>
					)}
				</div>

				<div className={styles.body}>
					{sections.map((section, si) => (
						<div key={si} className={styles.section}>
							{section.title && (
								<div
									style={
										accentColor ? ({ '--primary': accentColor } as React.CSSProperties) : undefined
									}
								>
									<FormSectionTitle>{section.title}</FormSectionTitle>
								</div>
							)}
							{isEditable && section.editContent ? (
								section.editContent
							) : (
								<div className={styles.viewGrid}>
									{section.fields.map((field, fi) => (
										<div
											key={fi}
											className={`${styles.viewField}${field.span === 'full' ? ` ${styles.viewFieldFull}` : ''}`}
										>
											{(field.showLabel ?? true) && (
												<span className={styles.viewLabel}>{field.label}</span>
											)}
											<div className={styles.viewValue}>{field.value}</div>
										</div>
									))}
								</div>
							)}
						</div>
					))}
				</div>

				{isEditable ? (
					<div className={styles.footer}>
						<Button
							variant="primary"
							icon={<CheckIcon className={styles.checkIcon} />}
							className={styles.submitBtn}
							onClick={onSubmit}
						>
							Enviar
						</Button>
					</div>
				) : (
					footer && <div className={styles.footer}>{footer}</div>
				)}
			</div>
		</div>
	);
};
